// Reunderwriting Module (Tier 3) PR 3 — pipeline page.
//
// Cloned from pages/deals/PipelinePage. Differences vs Deal Flow:
//   - Columns are the Reunderwriting stage set (Underwriting → Closing →
//     Closed); no Intake / Pre-Approval.
//   - No "+ New" wizard — reunderwriting cases are only ever launched
//     from the Loan-detail page (PR 1). The header CTA explains where
//     to start, mirroring the spec's "entry path" guidance.
//   - Filters: search / stage / event type / assigned A1 / include
//     archived / include closed.

import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Filter, Info, Kanban, Table as TableIcon, X } from "lucide-react";
import { Card, FormField, Input, Badge } from "@/design-system/components/ui";
import { Select } from "@/components/ui/Select";
import { PageHeader } from "@/components/layout/PageHeader";
import { DataTable, type Column as DataTableColumn } from "@/components/layout/DataTable";
import {
  useReunderwritingCases,
  useReunderwritingEventTypes,
} from "@/hooks/reunderwriting/useReunderwriting";
import { useReunderwritingV1 } from "@/hooks/reunderwriting/useFeatureFlag";
import {
  REUNDERWRITING_ACTIVE_STAGES,
  REUNDERWRITING_STAGE_LABEL,
  REUNDERWRITING_SUB_STATE_LABEL,
  type ReunderwritingCaseListItemDto,
  type ReunderwritingCaseListQuery,
  type ReunderwritingStage,
} from "@/types/reunderwriting";
import { useAssignableUsers } from "@/hooks/useAssignableUsers";
import { useAuth } from "@/hooks/useAuth";

type ViewMode = "kanban" | "table";

export function ReunderwritingPipelinePage() {
  const flagOn = useReunderwritingV1();
  const [view, setView] = useState<ViewMode>("kanban");
  const [filters, setFilters] = useState<ReunderwritingCaseListQuery>(() => {
    let includeClosed = false;
    try {
      includeClosed = localStorage.getItem("reunderwriting.includeClosed") === "true";
    } catch {
      // ignore
    }
    return { pageSize: 200, includeArchived: false, includeClosed };
  });

  if (!flagOn) {
    return (
      <Card>
        <h2 className="font-display font-bold text-h2">Reunderwriting disabled</h2>
        <p className="text-small text-gray-mid">
          The Reunderwriting module is not enabled in this environment.
          Toggle <code>VITE_FEATURE_REUNDERWRITING_V1=true</code> to enable.
        </p>
      </Card>
    );
  }

  return (
    <div className="flex flex-col gap-space-4" data-testid="reunderwriting-pipeline">
      <PageHeader
        eyebrow="Reunderwriting"
        title="Pipeline"
        description="Cross-stage Kanban + table view of every active reunderwriting case."
      />

      <Card>
        <div className="flex items-start gap-space-2">
          <Info size={16} className="text-gold-shadow shrink-0 mt-[2px]" aria-hidden />
          <p className="text-small m-0">
            A reunderwriting case is always launched from a loan. Open a loan
            and use <strong>&quot;Send to reunderwriting&quot;</strong> to start
            one; cases land here in the <em>Underwriting</em> column.
          </p>
        </div>
      </Card>

      <FilterBar value={filters} onChange={setFilters} />

      <div className="flex items-center justify-between">
        <ViewToggle value={view} onChange={setView} />
      </div>

      {view === "kanban" ? (
        <KanbanBoard query={filters} />
      ) : (
        <CaseTable query={filters} />
      )}
    </div>
  );
}

// ----------------------------------------------------------------------
// Filter bar
// ----------------------------------------------------------------------

function FilterBar({
  value,
  onChange,
}: {
  value: ReunderwritingCaseListQuery;
  onChange: (v: ReunderwritingCaseListQuery) => void;
}) {
  const stage = value.stage?.[0];
  const events = useReunderwritingEventTypes();

  const chips: { key: string; label: string; onRemove: () => void }[] = [];
  if (stage) {
    chips.push({
      key: "stage",
      label: `Stage: ${REUNDERWRITING_STAGE_LABEL[stage]}`,
      onRemove: () => onChange({ ...value, stage: undefined }),
    });
  }
  if (value.eventTypeCode) {
    chips.push({
      key: "eventType",
      label: `Event: ${value.eventTypeCode}`,
      onRemove: () => onChange({ ...value, eventTypeCode: undefined }),
    });
  }
  if (value.assignedA1Id) {
    chips.push({
      key: "a1",
      label: `Assigned A1: ${value.assignedA1Id}`,
      onRemove: () => onChange({ ...value, assignedA1Id: undefined }),
    });
  }
  if (value.includeArchived) {
    chips.push({
      key: "archived",
      label: "Including archived",
      onRemove: () => onChange({ ...value, includeArchived: false }),
    });
  }
  if (value.includeClosed) {
    chips.push({
      key: "closed",
      label: "Including closed",
      onRemove: () => {
        try { localStorage.setItem("reunderwriting.includeClosed", "false"); } catch { /* ignore */ }
        onChange({ ...value, includeClosed: false });
      },
    });
  }

  return (
    <Card>
      <div className="flex items-center gap-space-2 mb-space-3">
        <Filter size={14} className="text-gold-shadow" aria-hidden />
        <span className="font-display text-label font-semibold uppercase tracking-label text-gold-shadow">
          Filters
        </span>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-space-3">
        <FormField label="Search">
          <Input
            placeholder="Case #, loan #, event type…"
            value={value.q ?? ""}
            onChange={(e) => onChange({ ...value, q: e.target.value || undefined })}
            data-testid="reunderwriting-filter-search"
          />
        </FormField>

        <FormField label="Stage">
          <Select
            value={stage ?? ""}
            onChange={(e) =>
              onChange({
                ...value,
                stage: e.target.value
                  ? [e.target.value as ReunderwritingStage]
                  : undefined,
              })
            }
            data-testid="reunderwriting-filter-stage"
          >
            <option value="">All stages</option>
            {REUNDERWRITING_ACTIVE_STAGES.map((s) => (
              <option key={s} value={s}>
                {REUNDERWRITING_STAGE_LABEL[s]}
              </option>
            ))}
          </Select>
        </FormField>

        <FormField label="Event type">
          <Select
            value={value.eventTypeCode ?? ""}
            onChange={(e) =>
              onChange({ ...value, eventTypeCode: e.target.value || undefined })
            }
            data-testid="reunderwriting-filter-eventtype"
          >
            <option value="">All event types</option>
            {(events.data ?? []).map((et) => (
              <option key={et.eventTypeCode} value={et.eventTypeCode}>
                {et.eventTypeCode}
              </option>
            ))}
          </Select>
        </FormField>

        <A1Filter
          value={value.assignedA1Id ?? ""}
          onChange={(next) => onChange({ ...value, assignedA1Id: next || undefined })}
        />
      </div>

      <div className="mt-space-3 flex items-center gap-space-3">
        <label
          className="inline-flex items-center gap-space-2 text-small text-onyx cursor-pointer"
          data-testid="reunderwriting-include-closed-toggle"
        >
          <input
            type="checkbox"
            checked={!!value.includeClosed}
            onChange={(e) => {
              const next = e.target.checked;
              try { localStorage.setItem("reunderwriting.includeClosed", String(next)); }
              catch { /* ignore */ }
              onChange({ ...value, includeClosed: next });
            }}
          />
          Show closed cases
        </label>
        <label
          className="inline-flex items-center gap-space-2 text-small text-onyx cursor-pointer"
          data-testid="reunderwriting-include-archived-toggle"
        >
          <input
            type="checkbox"
            checked={!!value.includeArchived}
            onChange={(e) =>
              onChange({ ...value, includeArchived: e.target.checked })
            }
          />
          Show archived
        </label>
      </div>

      {chips.length > 0 && (
        <div className="mt-space-3 flex items-center gap-space-2 flex-wrap">
          {chips.map((c) => (
            <span
              key={c.key}
              className="inline-flex items-center gap-space-1 px-space-2 py-[3px] rounded-pill border border-gold/40 bg-gold/5 text-caption text-onyx"
            >
              {c.label}
              <button
                type="button"
                onClick={c.onRemove}
                aria-label={`Remove ${c.label}`}
                className="hover:text-status-error"
              >
                <X size={12} aria-hidden />
              </button>
            </span>
          ))}
        </div>
      )}
    </Card>
  );
}

function A1Filter({
  value,
  onChange,
}: {
  value: string;
  onChange: (next: string) => void;
}) {
  const { user } = useAuth();
  const usersQuery = useAssignableUsers("A1");

  return (
    <FormField label="Assigned A1">
      <Select value={value} onChange={(e) => onChange(e.target.value)}>
        <option value="">All A1s</option>
        {user?.id && (
          <option value={user.id}>Me ({user.displayName ?? "me"})</option>
        )}
        {(usersQuery.data ?? []).map((u) => (
          <option key={u.id} value={u.id}>
            {u.displayName}
          </option>
        ))}
      </Select>
    </FormField>
  );
}

// ----------------------------------------------------------------------
// View toggle
// ----------------------------------------------------------------------

function ViewToggle({ value, onChange }: { value: ViewMode; onChange: (v: ViewMode) => void }) {
  const base = "inline-flex items-center gap-space-1 px-space-3 h-8 text-label font-display uppercase tracking-button rounded-md";
  return (
    <div className="inline-flex border border-border-light rounded-md overflow-hidden">
      <button
        type="button"
        onClick={() => onChange("kanban")}
        className={
          base +
          (value === "kanban"
            ? " bg-onyx text-gold"
            : " bg-white-pure text-gray-mid hover:text-onyx")
        }
        aria-pressed={value === "kanban"}
      >
        <Kanban size={14} aria-hidden /> Kanban
      </button>
      <button
        type="button"
        onClick={() => onChange("table")}
        className={
          base +
          (value === "table"
            ? " bg-onyx text-gold"
            : " bg-white-pure text-gray-mid hover:text-onyx")
        }
        aria-pressed={value === "table"}
      >
        <TableIcon size={14} aria-hidden /> Table
      </button>
    </div>
  );
}

// ----------------------------------------------------------------------
// Kanban board
// ----------------------------------------------------------------------

function KanbanBoard({ query }: { query: ReunderwritingCaseListQuery }) {
  const casesQuery = useReunderwritingCases(query);
  const rows = casesQuery.data?.data ?? [];

  const byStage = useMemo(() => {
    const acc: Record<string, ReunderwritingCaseListItemDto[]> = {};
    for (const s of REUNDERWRITING_ACTIVE_STAGES) acc[s] = [];
    for (const r of rows) {
      if (acc[r.stage]) acc[r.stage].push(r);
    }
    return acc;
  }, [rows]);

  if (casesQuery.isLoading) {
    return (
      <Card>
        <p className="text-small text-gray-mid italic m-0">Loading cases…</p>
      </Card>
    );
  }
  if (casesQuery.isError) {
    return (
      <Card>
        <p className="text-small text-status-error m-0">
          Could not load reunderwriting cases. The <code>reunderwriting-v1</code>{" "}
          backend flag may be off.
        </p>
      </Card>
    );
  }

  return (
    <div
      className="grid grid-cols-1 md:grid-cols-3 gap-space-3"
      data-testid="reunderwriting-kanban"
    >
      {REUNDERWRITING_ACTIVE_STAGES.map((s) => (
        <Column key={s} stage={s} cases={byStage[s] ?? []} />
      ))}
    </div>
  );
}

function Column({
  stage,
  cases,
}: {
  stage: ReunderwritingStage;
  cases: ReunderwritingCaseListItemDto[];
}) {
  return (
    <section
      className="flex flex-col bg-white-warm rounded-md border border-border-light min-h-[400px]"
      data-testid={`reunderwriting-kanban-column-${stage}`}
    >
      <header className="px-space-3 py-space-2 border-b border-border-light flex items-center justify-between">
        <span className="font-display text-label uppercase tracking-label text-gold-shadow">
          {REUNDERWRITING_STAGE_LABEL[stage]}
        </span>
        <Badge status="archived">{cases.length}</Badge>
      </header>
      <ul className="flex flex-col gap-space-2 p-space-2 list-none m-0">
        {cases.map((c) => (
          <CaseCard key={c.id} rcase={c} />
        ))}
        {cases.length === 0 && (
          <li className="text-caption text-gray-mid italic px-space-2 py-space-3">
            No cases in this stage.
          </li>
        )}
      </ul>
    </section>
  );
}

function CaseCard({ rcase }: { rcase: ReunderwritingCaseListItemDto }) {
  const days = Math.max(
    0,
    Math.floor((Date.now() - new Date(rcase.stageEnteredDate).getTime()) / 86400000),
  );
  return (
    <li>
      <Link
        to={`/reunderwriting/cases/${rcase.id}`}
        className="block bg-white-pure rounded-md border border-border-light px-space-3 py-space-2 hover:border-gold/60 transition-colors"
        data-testid={`reunderwriting-case-card-${rcase.caseNumber}`}
      >
        <div className="flex items-center justify-between gap-space-2 mb-space-1">
          <span className="font-mono text-caption text-gold-shadow">{rcase.caseNumber}</span>
          <Badge status="draft">{rcase.eventTypeCode}</Badge>
        </div>
        <p className="text-small text-onyx font-semibold m-0 truncate">
          {rcase.subjectLoanNumber ?? rcase.subjectLoanId}
        </p>
        <p className="text-caption text-gray-mid m-0 mt-space-1">
          {REUNDERWRITING_SUB_STATE_LABEL[rcase.subState]} ·{" "}
          {rcase.assignedA1Id ? `A1: ${rcase.assignedA1Id}` : "A1 unassigned"} ·{" "}
          {days}d
        </p>
      </Link>
    </li>
  );
}

// ----------------------------------------------------------------------
// Table view
// ----------------------------------------------------------------------

function CaseTable({ query }: { query: ReunderwritingCaseListQuery }) {
  const casesQuery = useReunderwritingCases(query);
  const rows = casesQuery.data?.data ?? [];

  const columns: DataTableColumn<ReunderwritingCaseListItemDto>[] = [
    {
      key: "caseNumber",
      header: "Case #",
      render: (c) => (
        <Link
          to={`/reunderwriting/cases/${c.id}`}
          className="font-mono text-caption text-gold-shadow hover:text-gold-highlight"
        >
          {c.caseNumber}
        </Link>
      ),
    },
    {
      key: "eventType",
      header: "Event",
      render: (c) => <span className="text-small">{c.eventTypeCode}</span>,
    },
    {
      key: "subjectLoan",
      header: "Subject loan",
      render: (c) => (
        <Link
          to={`/portfolio-admin/loans/${c.subjectLoanId}`}
          className="text-small hover:text-gold-shadow"
        >
          {c.subjectLoanNumber ?? c.subjectLoanId}
        </Link>
      ),
    },
    {
      key: "stage",
      header: "Stage",
      render: (c) => <span className="text-small">{REUNDERWRITING_STAGE_LABEL[c.stage]}</span>,
    },
    {
      key: "subState",
      header: "Sub-state",
      render: (c) => (
        <span className="text-small text-gray-mid">
          {REUNDERWRITING_SUB_STATE_LABEL[c.subState]}
        </span>
      ),
    },
    {
      key: "a1",
      header: "A1",
      render: (c) => <span className="text-small">{c.assignedA1Id ?? "—"}</span>,
    },
    {
      key: "days",
      header: "Days in stage",
      render: (c) => {
        const days = Math.max(
          0,
          Math.floor(
            (Date.now() - new Date(c.stageEnteredDate).getTime()) / 86400000,
          ),
        );
        return <Badge status={days > 14 ? "error" : "archived"}>{days}d</Badge>;
      },
    },
  ];

  return (
    <DataTable
      columns={columns}
      rows={rows}
      loading={casesQuery.isLoading}
      error={casesQuery.isError ? "Could not load cases." : null}
      rowKey={(c) => c.id}
      emptyMessage="No cases match your current filters."
    />
  );
}
